import React from 'react'
import { graphql } from 'gatsby'
import styled from 'styled-components'
import { color } from '@primer-io/goat'

import * as Article from 'components/ArticleComponents'

import ApiEndpoint from 'components/ApiEndpoint/ApiEndpoint'
import { Renderer } from 'components/CustomMDX'
import { Section, PaymentMethodGuide, ClientSessionParams, ConditionalMDXSection } from './components'
import { Strong } from 'ds/TextGoat'
import Box from 'ds/Box'
import Grid from 'ds/Grid'
import Clickable from 'components/Clickable'
import { H4, BodySmall } from 'ds/TextGoat'
import Spacer from 'components/Spacer'

import HeadlessCheckoutImg from './assets/headless-checkout.png'
import DropInImg from './assets/drop-in.png'
import CapabilityTable from './components/CapabilityTable'

const Cover = styled.div`
	display: block;
	aspect-ratio: 1.829;
	margin: -20px -20px 4px;

	background: url(${p => p.img});
	background-size: cover;
`

const ImplementationLinkRoot = styled(Box).attrs(() => ({
	gap: 12,
	padding: 20,
	justify: 'flex-start',
}))`
	color: initial !important;
	text-decoration: initial !important;

	${p =>
		p.disabled &&
		`
		pointer-events: none;
		opacity: 0.8;
	`}
`

const ImplementationLink = ({ img, title, description, link, disabled }) => (
	<ImplementationLinkRoot as={Clickable} to={disabled ? undefined : link} disabled={disabled}>
		<Cover img={img} />
		<H4 as='h4'>{title}</H4>
		<BodySmall>{description}</BodySmall>
	</ImplementationLinkRoot>
)

const PaymentMethodProcessorTemplate = ({ data, location, pageContext }) => {
	const { processorId } = pageContext
	const [paymentMethod] = data.allPaymentMethod.nodes
	const processor = paymentMethod?.processors?.find(({ id }) => id === processorId) ?? {}

	const { paymentMethodId } = paymentMethod

	const {
		name,
		paymentMethodType,
		fullPaymentMethodName,
		description,
		capabilityTable: capabilities,
		additionalClientSessionParameters,
		directIntegration,
		connectInstructions,
	} = processor

	if (!fullPaymentMethodName) throw `Please provide fullPaymentMethodName in the yaml file.`

	const paymentMethodGuideProps = {
		location,
		paymentMethod,
		processor,
		description: `Let customers securely make payments using ${fullPaymentMethodName} on your website or mobile application.`,
	}

	const dashboardUrl = `https://sandbox-dashboard.primer.io/connections/marketplace/detail`

	return (
		<PaymentMethodGuide {...paymentMethodGuideProps}>
			<ConditionalMDXSection content={description} topLevel={2} />

			<CapabilityTable capabilities={capabilities} />

			<Section title={`Get started with ${fullPaymentMethodName}`}>
				{!connectInstructions?.body && (
					<>
						<Article.H3>Before you begin</Article.H3>
						<Article.P>
							If this is your first payment method, complete the{' '}
							<Article.A href='/payments/process-payments'>"Process Payments" guide</Article.A> to begin processing
							payments with Primer.
						</Article.P>
						<Article.H3>Connect {fullPaymentMethodName} to Primer</Article.H3>
						<Article.OrderedList>
							{paymentMethodType !== 'PAYMENT_CARD' && directIntegration && (
								<li>
									<Article.P>
										<Strong>Add {paymentMethod.name} payment method to your Dashboard</Strong>
									</Article.P>
									<Article.P>
										Go to the{' '}
										<Article.A href={'https://sandbox-dashboard.primer.io/connections/discover'}>Connections</Article.A>{' '}
										section of the Dashboard and look for{' '}
										<Article.A href={`${dashboardUrl}/${paymentMethodId}`}>{paymentMethod.name}</Article.A>.
									</Article.P>
									<Article.P>
										Follow the instructions to connect your {paymentMethod.name} processor account to your Primer
										Dashboard.
									</Article.P>
								</li>
							)}
							{paymentMethodType !== 'PAYMENT_CARD' && !directIntegration && (
								<li>
									<Article.P>
										<Strong>
											Add {paymentMethod.name} payment method to your {name} account
										</Strong>
									</Article.P>
									<Article.P>
										Go to your {name} account and add the payment method {paymentMethod.name}.
									</Article.P>
								</li>
							)}
							{paymentMethodType !== 'PAYMENT_CARD' && !directIntegration && (
								<li>
									<Article.P>
										<Strong>Add {name} to your Dashboard</Strong>
									</Article.P>
									<Article.P>
										Go to the{' '}
										<Article.A href={'https://sandbox-dashboard.primer.io/connections/discover'}>Connections</Article.A>{' '}
										section of the Dashboard and look for{' '}
										<Article.A href={`${dashboardUrl}/${processor.id}`}>{name}</Article.A>.
									</Article.P>
									<Article.P>
										Follow the instructions to connect your {name} processor account to your Primer Dashboard.
									</Article.P>
								</li>
							)}
							<li>
								<Article.P>
									<Strong>Activate {fullPaymentMethodName} on the Checkout</Strong>
								</Article.P>
								<Article.P>
									Go to the <Article.A href={'https://sandbox-dashboard.primer.io/checkout'}>Checkout</Article.A>{' '}
									section of the Dashboard and activate the {paymentMethod.name} payment method.
								</Article.P>
								<Article.P>
									Then go to the settings view of {paymentMethod.name}, and make sure that {paymentMethod.name} is
									properly set up to use your {name} processor account.
								</Article.P>
							</li>
							<li>
								<Article.P>
									<Strong>Configure a Workflow to process payments made with {fullPaymentMethodName}</Strong>
								</Article.P>
								<Article.P>
									Go to the <Article.A href={'https://sandbox-dashboard.primer.io/workflow'}>Workflow</Article.A> make
									sure your {name} processor is used to process payments made with {fullPaymentMethodName}.
								</Article.P>
							</li>
						</Article.OrderedList>
					</>
				)}

				{connectInstructions?.body && <ConditionalMDXSection content={connectInstructions} topLevel={3} />}

				<Article.H3>Prepare the client session</Article.H3>
				<Article.P>{fullPaymentMethodName} leverages the following parameters to process a payment.</Article.P>
				<Article.P>Pass them when creating the client session.</Article.P>
				<ClientSessionParams additionalParams={additionalClientSessionParameters} />

				<Article.HR />

				<Article.H2>Add {fullPaymentMethodName} into your website or mobile app</Article.H2>
				<Spacer.V small='32px' />
				<Grid cols={{ lg: 2, xl: 2 }} gap={16}>
					{processor?.dropIn && (
						<ImplementationLink
							link={`./drop-in`}
							title='Drop-in UI'
							description={'Use our customizable drop-in UI to quickly add payment methods to your checkout.'}
							img={DropInImg}
						/>
					)}

					{processor?.headless && (
						<ImplementationLink
							link={`./headless`}
							title='Headless Checkout'
							description='Have the freedom to build your own UI using our headless checkout.'
							img={HeadlessCheckoutImg}
						/>
					)}
					{!processor?.headless && (
						<ImplementationLink
							disabled
							link={`./headless`}
							title='Headless Checkout'
							description={
								<div>
									<div>Have the freedom to build your own UI using our headless checkout.</div>
									<br />
									<i>Documentation coming soon!</i>
								</div>
							}
							img={HeadlessCheckoutImg}
						/>
					)}
				</Grid>
			</Section>
		</PaymentMethodGuide>
	)
}

export const query = graphql`
	query ($id: String!, $processorId: String!) {
		allPaymentMethod(filter: { id: { eq: $id }, processors: { elemMatch: { id: { eq: $processorId } } } }) {
			nodes {
				# paymentMethodId
				name
				slug
				icon
				processors {
					id
					name
					slug
					icon
					directIntegration
					paymentMethodType
					fullPaymentMethodName
					directIntegration
					connectInstructions {
						body
					}
					capabilityTable {
						id
						value
					}

					description {
						body
					}
					additionalClientSessionParameters {
						name
						required
						description {
							body
						}
					}

					dropIn {
						description {
							body
						}
					}

					headless {
						description {
							body
						}
					}
				}
			}
		}
	}
`

export default PaymentMethodProcessorTemplate
